import { Component, computed, inject, OnDestroy, OnInit, signal, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Tag } from 'primeng/tag';
import { Router } from '@angular/router';
import { SortEvent } from 'primeng/api';
import { finalize, of, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DynamicFilterConfig, DynamicFilterValues, EnumValues, createDynamicFilterState } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { UserGroup } from '../../../../../models/authorization/enum/user-group.enum';
import { LocalHelperService } from '../../../../../services/local-helper.service';
import { EnumService } from '../../../../../services/enum.service';
import { DispatchRequestProxyService } from '../../../../../proxies/inventory/dispatch-request.proxy.service';
import { DispatchRequest, StatusDispatchRequest } from '../../../../../models/dispatch-request/dispatch-request';
import { DispatchRequestSearchCommand } from '../../../../../models/inventory/dispatch-request-search';
import { SupplyWarehouseDocumentType } from '../../../../../models/supply-warehouse/supply-warehouse-document-type.enum';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { DepartmentProxyService } from '../../../../../proxies/user-master';
import { Department } from '../../../../../models/user-master/department';

interface DepartmentOption {
  id: string;
  name: string;
}

@Component({
  selector: 'app-completed-dispatch-tab',
  templateUrl: './completed-dispatch-tab.component.html',
  styleUrl: './completed-dispatch-tab.component.scss',
})
export class CompletedDispatchTabComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private enumService = inject(EnumService);
  private dispatchRequestService = inject(DispatchRequestProxyService);
  private departmentProxyService = inject(DepartmentProxyService);
  public localHelperService = inject(LocalHelperService);

  private destroy$ = new Subject<void>();
  private appliedFilters: DynamicFilterValues = {};
  // Skips redundant searches the library triggers on autocomplete select/clear when the value did not change.
  private lastFilterKey = '';

  readonly canCreateInternalReturn = AuthService.hasRole(UserGroup.SupplyWarehouseInternalReturnsAdmin);
  readonly LOCATION_STORAGE_KEY = 'supply-warehouse-selected-location';
  readonly CONSUMABLES_CODE = 'NT.WH-CON-1';

  isLoading = signal<number>(0);
  showFilters = signal<boolean>(true);
  dispatchList = signal<DispatchRequest[]>([]);
  departments = signal<DepartmentOption[]>([]);
  filteredDepartments = signal<DepartmentOption[]>([]);
  private departmentsLoaded = signal(false);

  filterConfig = signal<DynamicFilterConfig | null>(null);
  private readonly filterConfigWithOptions = computed(() => (this.departmentsLoaded() ? this.filterConfig() : null));
  readonly filterState = createDynamicFilterState({
    storageKey: 'supply-warehouse-completed-dispatch-filters',
    config: this.filterConfigWithOptions,
    paramPrefix: 'cd.',
  });
  statusesDispatch = signal<EnumValues>({});
  enumsReady = signal(false);

  totalRecords = 0;
  currentPage = 1;
  pageSize = 25;
  sortField: string | undefined = undefined;
  sortOrder = -1;

  get selectedLocation(): string {
    return localStorage.getItem(this.LOCATION_STORAGE_KEY) || this.CONSUMABLES_CODE;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        const statusValues = this.enumService.getEnum('StatusDispatchRequest', 'inventory').values;
        if (Object.keys(statusValues).length > 0) {
          this.statusesDispatch.set(statusValues);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.loadDepartments();
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.tryInitializeFilterConfig());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang && this.enumsReady()) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const filteredDepartments = this.filteredDepartments;
    const translate = (key: string, fallback: string) => {
      const t = this.translateService.instant(key);
      return t === key ? fallback : t;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('Dispatch_Request_Number', 'DR Number'),
          placeholder: translate('Search_DR_number', 'Search DR number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'locationCode',
          label: translate('Dispatch_Request_Location', 'Location'),
          placeholder: translate('Search_location', 'Search location'),
          defaultVisible: false,
        },
        {
          type: 'text',
          formControlName: 'supplyRequestNumber',
          label: translate('Supply_Request_Number', 'Supply Request Number'),
          placeholder: translate('Search_supply_request_number', 'Search supply request number'),
          defaultVisible: false,
        },
        {
          type: 'autocomplete',
          formControlName: 'department',
          label: translate('Common_Department', 'Department'),
          placeholder: translate('Select_department', 'Select department'),
          autocompleteConfig: {
            get suggestions() {
              return filteredDepartments();
            },
            optionLabel: 'name',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterDepartments(event),
            resolveById: (id: string) => of(this.departments().find((department) => department.id === id) ?? null),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private extractDepartmentId(departmentValue: any): string | undefined {
    if (!departmentValue) return undefined;
    if (typeof departmentValue === 'string') return departmentValue;
    return departmentValue.id;
  }

  search(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formValues: any = this.appliedFilters;
    const number = formValues.number?.trim();
    const locationCode = formValues.locationCode?.trim();
    const supplyRequestNumber = formValues.supplyRequestNumber?.trim();
    const departmentId = this.extractDepartmentId(formValues.department);

    const command: DispatchRequestSearchCommand = {
      number: number || undefined,
      locationCode: locationCode || this.selectedLocation,
      status: StatusDispatchRequest.Completed,
      supplyRequestNumber: supplyRequestNumber || undefined,
      departmentReference: departmentId || undefined,
    };

    this.isLoading.update((v) => v + 1);
    this.dispatchRequestService
      .search(command, this.currentPage, this.pageSize, this.sortField, this.sortOrder)
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => {
          this.dispatchList.set(response.data?.items ?? []);
          this.totalRecords = response.data?.totalCount ?? 0;
        },
        error: () => {
          this.dispatchList.set([]);
          this.totalRecords = 0;
        },
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPageChange(event: any): void {
    this.currentPage = (event.page ?? 0) + 1;
    this.pageSize = event.rows ?? this.pageSize;
    this.search();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private buildFilterKey(filters: any): string {
    const departmentId = this.extractDepartmentId(filters.department);
    return JSON.stringify({
      number: (filters.number ?? '').toString().trim(),
      locationCode: (filters.locationCode ?? '').toString().trim(),
      supplyRequestNumber: (filters.supplyRequestNumber ?? '').toString().trim(),
      department: departmentId ?? '',
    });
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const key = this.buildFilterKey(filters);
    if (key === this.lastFilterKey) {
      return;
    }
    this.lastFilterKey = key;
    this.appliedFilters = filters;
    this.currentPage = 1;
    this.search();
  }

  private loadDepartments(): void {
    this.departmentProxyService.getAll().subscribe({
      next: (response) => {
        const options: DepartmentOption[] = (response.data ?? []).map((dep: Department) => ({
          id: dep.id,
          name: this.localHelperService.getLocalizedValue(dep.name),
        }));
        this.departments.set(options);
        this.filteredDepartments.set(options);
        this.departmentsLoaded.set(true);
      },
      error: () => {
        this.departments.set([]);
        this.filteredDepartments.set([]);
        this.departmentsLoaded.set(true);
      },
    });
  }

  filterDepartments(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    const filtered = this.departments().filter((dep) => dep.name?.toLowerCase().includes(query));
    this.filteredDepartments.set(filtered);
  }

  getDepartmentName(departmentReference?: string): string {
    if (!departmentReference) return '-';
    const dept = this.departments().find((d) => d.id === departmentReference);
    return dept?.name || departmentReference;
  }

  onTableSort(event: SortEvent): void {
    const newField = event.field ?? undefined;
    const newOrder = event.order ?? -1;
    if (newField === this.sortField && newOrder === this.sortOrder) return;
    this.sortField = newField;
    this.sortOrder = newOrder;
    this.currentPage = 1;
    this.search();
  }

  getStatusLabel(status: StatusDispatchRequest): string {
    const enumName = StatusDispatchRequest[status];
    const key = `Common_Status_${enumName}`;
    const result = this.translateService.instant(key);
    return result !== key ? result : (this.statusesDispatch()[status] as string) || '';
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getStatusSeverity(_status: StatusDispatchRequest): Tag['severity'] {
    return 'success';
  }

  navigateToDetail(dispatchRequestId: string): void {
    this.router.navigate(['/supply-warehouse-detail'], {
      queryParams: {
        documentType: SupplyWarehouseDocumentType.DispatchRequest,
        id: dispatchRequestId,
      },
    });
  }

  navigateToInternalReturn(dispatchRequestId: string): void {
    this.router.navigate(['/supply-warehouse-detail'], {
      queryParams: {
        documentType: SupplyWarehouseDocumentType.InternalReturn,
        id: dispatchRequestId,
      },
    });
  }
}
